import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _max from 'lodash/max';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import Card from 'components/core/Card';
import NumberInputField from 'components/inputFields/NumberInputField';
import Tooltip from 'components/core/Tooltip';
import Typography from 'components/core/Typography';
// Models
import mParkingCategory from 'models/cpm/parkingCategory';
import mCarpark from 'models/cpm/carpark';
// Custom
import AppIcon from 'components/icons/AppIcon';
import { sanitizeInt } from 'utils/Tools';
import { errorShape } from 'constants/PropTypesShapes';
import { buildFormErrors, cpmCarparkSettingsSpacesConstraints } from 'utils/Forms';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';

class CarparkSpacesSettingsEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleCapacityChange = this.handleCapacityChange.bind(this);
    this.handleValidate = this.handleValidate.bind(this);

    // Initialize without booking capacity
    this.doSetWithoutBookingCapacity();
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers
  handleCapacityChange(spaceType, value) {
    const { onFieldsChange } = this.props;
    onFieldsChange({
      [spaceType]: value,
    }, () => {
      this.doSetWithoutBookingCapacity();
    });
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: { bookingCapacity, assignedCapacity },
      onErrorsChange, onValidate,
    } = this.props;

    const errors = buildFormErrors({ bookingCapacity, assignedCapacity }, cpmCarparkSettingsSpacesConstraints);

    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate(mCarpark.serializeSettingsSpaces({ bookingCapacity, assignedCapacity }));
      return true;
    }
    return false;
  }

  // Private
  doSetWithoutBookingCapacity() {
    const { capacity, fields: { bookingCapacity, assignedCapacity }, onFieldsChange } = this.props;
    onFieldsChange({
      withoutBookingCapacity: _max([(capacity - bookingCapacity - assignedCapacity), 0]),
    });
  }

  render() {
    const {
      fields, errors, capacity,
    } = this.props;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12">
            <div className="d-flex">
              <FormHelper
                elements={_map(mCarpark.spacesTypeList, (spaceType) => (
                  {
                    title: <LocalizedMessage id={spaceType.labelId} />,
                    content: <LocalizedMessage id={`help.carparkSpacesSettings.${spaceType.value}`} />,
                  }
                ))}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12">
                      <Card outlined defaultBackground>
                        <div className="row">
                          <div className="col-12 d-flex align-items-center justify-content-center">
                            <Typography type="subtitle" className="ml-2">
                              <LocalizedMessage id="title.totalParkingSpaces" />
                            </Typography>
                          </div>
                          <div className="col-12 pt-2 d-flex align-items-center justify-content-center">
                            <Typography type="keyInfo2">
                              {capacity}
                            </Typography>
                          </div>
                        </div>
                      </Card>
                    </div>
                    {_map(mCarpark.spacesTypeList, (spaceType) => (
                      <div key={spaceType.value} className="d-flex col-12 align-items-center">
                        <div style={{
                          width: 5, height: '70%', backgroundColor: spaceType.color, marginRight: 10,
                        }}
                        />
                        <NumberInputField
                          readOnly={spaceType.isWithoutBooking}
                          disabled={spaceType.isWithoutBooking}
                          required={!spaceType.isWithoutBooking}
                          sanitizer={sanitizeInt}
                          inputMaxWidth={70}
                          max={capacity}
                          label={<LocalizedMessage id={spaceType.labelId} />}
                          errorText={_get(errors, spaceType.value)}
                          value={fields[spaceType.value]}
                          onChange={(value) => this.handleCapacityChange(spaceType.value, value)}
                        />
                        {spaceType.isWithoutBooking && (
                          <Tooltip content={<LocalizedMessage id="tooltip.spacesWithoutBooking" />}>
                            <AppIcon id="btnInfo" defaultColor />
                          </Tooltip>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

CarparkSpacesSettingsEditForm.propTypes = {
  capacity: PropTypes.number.isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

CarparkSpacesSettingsEditForm.defaultProps = {
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  bookingCapacity: {
    resourceKey: 'bookingCapacity',
    defaultValue: 0,
  },
  assignedCapacity: {
    resourceKey: 'assignedCapacity',
    defaultValue: 0,
  },
  withoutBookingCapacity: {
    defaultValue: 0,
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.settings,
  operationError: {
    ...ownProps.operationError,
    messageValuesExtractors: {
      bookingCapacity: [
        {
          key: 'title',
          pattern: /(\d+)/,
          setTransform: (value, locOwnProps) => mParkingCategory.title(mParkingCategory.getParkingCategoryById(locOwnProps.parkingCategories, value)),
        },
      ],
      assignedCapacity: [
        {
          key: 'title',
          pattern: /(\d+)/,
          setTransform: (value, locOwnProps) => mParkingCategory.title(mParkingCategory.getParkingCategoryById(locOwnProps.parkingCategories, value)),
        },
      ],
    },
  },
});

const mapStateToProps = (state) => ({
  settings: _get(state.cpmCarpark.current.data, 'settings.spaces'),
  operationError: state.cpmCarpark.updateSpacesSettings.error,
  parkingCategories: state.cpmCarparkParkingCategory.list.elements,
});

module.exports = compose(
  connect(
    mapStateToProps,
  ),
  withFormManagement(
    mapFieldsToProps,
    mapOwnPropsToResources,
    false,
  ),
  withStateReinit(['updateCarparkSpacesSettings']),
)(CarparkSpacesSettingsEditForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/main/CarparkSpacesSettingsEditForm.js